import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { motion } from "framer-motion";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { fadeUp, stagger } from "@/lib/animations";
import { api } from "@/lib/api";
import Footer from "@/components/ui/Footer";
import AddToCartButton from "@/components/shop/AddToCartButton";

type Product = {
  id: string;
  slug: string;
  name: string;
  brand: string;
  description: string;
  price: number; // cents
  inventory: number;
  images: { mediaId: string; url: string | null; alt: string | null }[];
  imageUrl: string | null;
};

function formatPrice(cents: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

export function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    api
      .get<{ product: Product }>(`/api/products/${slug}`)
      .then((r) => setProduct(r.product))
      .catch((e: { status?: number; message?: string }) => {
        if (e.status === 404) setNotFound(true);
        else setErr(e.message ?? "Failed to load product");
      });
  }, [slug]);

  if (notFound)
    return (
      <div className="min-h-dvh flex flex-col" style={{ backgroundColor: colors.cream }}>
        <main className="flex-1 w-full pt-32 px-6 max-w-3xl mx-auto">
          <h1>Product not found</h1>
          <Link to="/shop">← Back to shop</Link>
        </main>
        <Footer />
      </div>
    );
  if (err)
    return (
      <div className="min-h-dvh flex flex-col" style={{ backgroundColor: colors.cream }}>
        <main
          className="flex-1 w-full pt-32 px-6 max-w-3xl mx-auto"
          style={{ color: colors.berryRose, fontWeight: 600 }}
        >
          {err}
        </main>
        <Footer />
      </div>
    );
  if (!product)
    return (
      <div className="min-h-dvh flex flex-col" style={{ backgroundColor: colors.cream }}>
        <main className="flex-1 w-full pt-32 px-6 max-w-3xl mx-auto">Loading…</main>
        <Footer />
      </div>
    );

  const heroImg = product.images[0]?.url ?? null;

  return (
    <div className="min-h-dvh flex flex-col" style={{ backgroundColor: colors.cream }}>
      <section className="flex-1 pt-28 pb-12 px-6">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="rounded-2xl overflow-hidden border"
            style={{ borderColor: `${colors.tan}25`, backgroundColor: "white" }}
          >
            {heroImg ? (
              <img
                src={heroImg}
                alt={product.name}
                className="w-full h-full object-cover min-h-[360px]"
              />
            ) : (
              <div
                className="w-full min-h-[360px]"
                style={{ backgroundColor: `${colors.tan}20` }}
              />
            )}
          </motion.div>

          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p
              variants={fadeUp}
              custom={0}
              className="text-xs tracking-[0.15em] uppercase mb-2"
              style={{ fontFamily: fonts.body, fontWeight: 700, color: `${colors.navy}66` }}
            >
              {product.brand}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              custom={1}
              className="text-4xl mb-3"
              style={{ fontFamily: fonts.logo, fontWeight: 400, color: colors.navy }}
            >
              {product.name}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              custom={2}
              className="text-2xl mb-6"
              style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.berryPink }}
            >
              {formatPrice(product.price)}
            </motion.p>
            <motion.p
              variants={fadeUp}
              custom={3}
              className="text-base leading-relaxed mb-8"
              style={{ fontFamily: fonts.body, fontWeight: 500, color: `${colors.navy}90` }}
            >
              {product.description}
            </motion.p>
            <motion.div variants={fadeUp} custom={4} className="flex items-center gap-3">
              <AddToCartButton
                product={{
                  productId: product.id,
                  slug: product.slug,
                  name: product.name,
                  brand: product.brand,
                  price: product.price,
                  imageUrl: heroImg,
                }}
                outOfStock={(product.inventory ?? 0) <= 0}
              />
              <Link
                to="/shop"
                className="text-sm underline"
                style={{ fontFamily: fonts.body, fontWeight: 600, color: colors.navy }}
              >
                Back to Shop
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
